/**
 * The left rail: a month you can see, and the moments falling in it.
 *
 * TWO CARDS, BECAUSE THE APPROVED DESIGN HAS TWO
 * -----------------------------------------------------------------------------
 * {@link CalendarCard} is the month grid. {@link MomentsCard} is the list. They
 * come from one read and one fold, which is why they share a module, and they
 * are two cards because the design the owner signed off on is two cards in a
 * 310px rail — not one wide card with a grid bolted to the side of a list.
 *
 * THE CALENDAR IS ALWAYS DRAWN, AND THAT IS A CORRECTION
 * -----------------------------------------------------------------------------
 * An earlier pass made `MonthGrid` return `null` when no day in the drawn month
 * carried a moment, on a measurement that 32% of days fall in such a month and
 * an undotted grid is words and pixels saying nothing.
 *
 * The measurement was right and the conclusion was wrong. The owner asked for
 * "a calendar kind of thing, to show which all holidays are coming", and on the
 * day they opened the page the calendar was **not there** — which is a worse
 * answer to "where is my calendar" than an undotted September. A calendar's job
 * is to show you the shape of the month whether or not anything is in it; that
 * is what distinguishes it from the list beside it.
 *
 * So it draws every day, and the card carries its own emptiness honestly: a
 * month with nothing in it says so under the grid, in five words, rather than
 * vanishing.
 *
 * TWO DOT COLOURS, AND THE LEGEND IS WHY THAT IS ALLOWED
 * -----------------------------------------------------------------------------
 * `warn` for a moment inside {@link CALENDAR_SOON_DAYS}, `brand-b` for one
 * later. On this page `warn` otherwise means "Amazon may review your account",
 * and a tinted **pill** borrowing it was removed from the opportunities card for
 * exactly that reason.
 *
 * A 4px dot under a date is not that pill, and the difference is the legend:
 * "Within a week" and "Later this month" are printed directly beneath the grid,
 * so the colour is labelled where it is used rather than carrying a meaning the
 * reader has to import from another card. Colour is never the only carrier here
 * either — every dotted day appears in the list beside it with its name, its
 * date and its countdown in text.
 */
import {
  CALENDAR_WINDOW_DAYS,
  daysBetween,
  type CalendarEntry,
  type CalendarResponse,
  MAX_OWN_MOMENT_NAME_LENGTH,
  OWN_MOMENT_COPY,
  addOwnMomentRequestSchema,
  ownMomentDateIsAddable,
  parseCalendarDate,
  type OwnMoment,
} from '@wend/shared';
import { useRef, useState, type FormEvent } from 'react';

import { Button, Card, CardContent, CardHeader, CardTitle, FormField, Input, cn } from '@wend/ui';
import { Link } from 'react-router-dom';

import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import type { AsyncData } from '../../lib/use-async-data';

/** Inside this many days a moment is drawn as imminent rather than upcoming. */
export const CALENDAR_SOON_DAYS = 7;

const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/**
 * One cell of the month grid. Every slot is a real day — the neighbouring
 * month's, greyed, rather than a hole.
 *
 * The mockup draws `.cal .d.out` for those; the first build left them blank. A
 * blank leading run reads as the month starting mid-air, and a blank trailing
 * run makes the card's height jump between a five-row and a six-row month.
 * Filling them costs nothing and a dot on 1 November, seen from the October
 * grid, is information rather than noise.
 */
export interface MonthCell {
  readonly day: number;
  readonly date: string;
  readonly isToday: boolean;
  readonly hasMoment: boolean;
  /** Inside `CALENDAR_SOON_DAYS` of today — drawn amber rather than violet. */
  readonly isSoon: boolean;
  /** False for the neighbouring month's days, which are greyed. */
  readonly inMonth: boolean;
}

/**
 * The cells for the month containing `today`, Monday-first.
 *
 * `today` arrives as the reader's **local** date from {@link localToday}; the
 * arithmetic inside is UTC only so a month's length and its first weekday are
 * computed without a zone shifting them. A date string names a day, and
 * `Date.UTC` is how to do calendar arithmetic on a day rather than on an instant.
 */
export function monthCells(
  today: string,
  entries: readonly CalendarEntry[],
  drawnMonth?: string,
): MonthCell[] {
  /*
   * `drawnMonth` is which month to draw; `today` still decides which cell is
   * today and which dots are imminent. They are the same date until the reader
   * pages forward, and separating them is what lets the arrows work without the
   * highlight wandering with them.
   */
  const shown = drawnMonth ?? today;
  /*
   * Both parts must be present and in range. `Number('')` is 0, not NaN, so a
   * bare `Number.isFinite` guard let an empty string through and produced a
   * thirty-one-cell grid for month zero of year zero.
   */
  const year = Number(shown.slice(0, 4));
  const month = Number(shown.slice(5, 7));
  if (!/^\d{4}-\d{2}-\d{2}$/.test(shown) || month < 1 || month > 12) return [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(today)) return [];
  const monthIndex = month - 1;

  const first = new Date(Date.UTC(year, monthIndex, 1));
  /* getUTCDay is Sunday-first; the grid is Monday-first, as the rest of the app is. */
  const leading = (first.getUTCDay() + 6) % 7;

  const withMoments = new Set(entries.map((entry) => entry.date));
  const cells: MonthCell[] = [];

  /*
   * Start `leading` days before the 1st and run until the week is complete.
   * `Date.UTC` normalises a negative day-of-month into the previous month and an
   * overrun into the next, so there is no month-length table and no year-end
   * special case.
   */
  const total = Math.ceil((leading + new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()) / 7) * 7;
  for (let slot = 0; slot < total; slot += 1) {
    const at = new Date(Date.UTC(year, monthIndex, 1 - leading + slot));
    const date = at.toISOString().slice(0, 10);
    const away = daysBetween(today, date);
    cells.push({
      day: at.getUTCDate(),
      date,
      isToday: date === today,
      hasMoment: withMoments.has(date),
      isSoon: withMoments.has(date) && away >= 0 && away <= CALENDAR_SOON_DAYS,
      inMonth: at.getUTCMonth() === monthIndex,
    });
  }
  return cells;
}

/** `days` after an ISO date, in UTC so no zone shifts it. */
export function addDays(iso: string, days: number): string {
  const at = Date.parse(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(at)) return iso;
  return new Date(at + days * 86_400_000).toISOString().slice(0, 10);
}

/** Whole months between the two dates' months, never negative. */
export function monthsBetween(fromIso: string, toIso: string): number {
  const months =
    (Number(toIso.slice(0, 4)) - Number(fromIso.slice(0, 4))) * 12 +
    (Number(toIso.slice(5, 7)) - Number(fromIso.slice(5, 7)));
  return Number.isFinite(months) && months > 0 ? months : 0;
}

const NAV_BUTTON = cn(
  'flex size-[26px] items-center justify-center rounded-[7px] border border-line',
  'font-mono text-[0.8rem] leading-none text-muted',
  'hover:border-line-strong hover:text-text',
  'disabled:pointer-events-none disabled:opacity-40',
);

/** The same day-of-month `offset` months away, clamped to that month's length. */
export function shiftMonth(iso: string, offset: number): string {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7)) - 1;
  const at = new Date(Date.UTC(year, month + offset, 1));
  return at.toISOString().slice(0, 10);
}

/** "today", "tomorrow", "in 12 days" — a countdown a person would say out loud. */
export function daysAwayLabel(daysAway: number): string {
  if (daysAway <= 0) return 'today';
  if (daysAway === 1) return 'tomorrow';
  return `in ${String(daysAway)} days`;
}

/**
 * The reader's own date, as their wall clock has it.
 *
 * **Not `toISOString().slice(0, 10)`**, which is the UTC date. At UTC+5:30 those
 * disagree for five and a half hours out of every twenty-four, and the version
 * that used the server's UTC date marked the 18th while the reader's date was
 * the 19th, with every countdown one too many.
 */
export function localToday(now: Date): string {
  return [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * The entries a reader on `today` should actually see, from what the API served.
 *
 * The API deliberately serves a day either side of the window so this cut can be
 * made against the reader's clock rather than the server's. Exported because it
 * is the single definition of "in the window" that the grid, the list and both
 * cards' emptiness tests all share.
 */
export function inReadersWindow(
  entries: readonly CalendarEntry[],
  today: string,
): { readonly entry: CalendarEntry; readonly daysAway: number }[] {
  return entries
    .map((entry) => ({ entry, daysAway: daysBetween(today, entry.date) }))
    .filter(
      (one) =>
        Number.isFinite(one.daysAway) && one.daysAway >= 0 && one.daysAway <= CALENDAR_WINDOW_DAYS,
    )
    .sort((a, b) => a.daysAway - b.daysAway);
}

/**
 * The same window cut, over the workspace's OWN moments.
 *
 * Identical arithmetic to {@link inReadersWindow}, typed over `OwnMoment`
 * instead of `CalendarEntry`. Written out rather than made generic on purpose:
 * `inReadersWindow` is exported and pinned by two suites, and widening its type
 * to `{ date: string }` would change a symbol those suites import in order to
 * prove something else entirely. A second small function is cheaper than a
 * shared one whose signature two other files depend on.
 *
 * The cut is the READER'S, for the reason the whole card takes `now`: the
 * server windows in UTC with a day of slack, and at UTC+05:30 the server's
 * today is the reader's yesterday for five and a half hours out of every
 * twenty-four. This is where that slack is spent.
 */
/**
 * `2026-11-08` -> `8 Nov 2026`, in UTC and never in the viewer's zone.
 *
 * The same rule `shortUtcDay` follows in `ActivityCharts`, and for the same
 * reason: a moment is a DAY. Formatted through a `Date` at local midnight it
 * would print the day before for every reader west of UTC, which on somebody's
 * launch date is the one error this card has already shipped once.
 *
 * The year is included, unlike the list above it. That list is cut to sixty
 * days so a year would be noise; "Your moments" holds every row a workspace
 * has, so two 8 Novembers a year apart are otherwise indistinguishable.
 */
export function formatMomentDate(date: string): string {
  return MOMENT_DATE_FORMAT.format(new Date(`${date}T00:00:00.000Z`));
}

const MOMENT_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

export function inOwnReadersWindow(
  moments: readonly OwnMoment[],
  today: string,
): readonly { moment: OwnMoment; daysAway: number }[] {
  return moments
    .map((moment) => ({ moment, daysAway: daysBetween(today, moment.date) }))
    .filter((one) => Number.isFinite(one.daysAway) && one.daysAway >= 0 && one.daysAway <= CALENDAR_WINDOW_DAYS)
    .sort((a, b) => (a.moment.date < b.moment.date ? -1 : a.moment.date > b.moment.date ? 1 : 0));
}


/** Everything both cards need, derived once from one response and one clock. */
function readerView(data: CalendarResponse | null, now: Date | undefined) {
  const today = localToday(now ?? new Date());
  const windowed = data === null ? [] : inReadersWindow(data.entries, today);
  /*
   * The workspace's own dates, cut to the same window by the same clock. They
   * arrive unwindowed — every row it holds, past included — because a row a
   * person cannot see is a row they cannot remove, and "Your moments" lists
   * the rest.
   */
  const own = data === null ? [] : inOwnReadersWindow(data.ownMoments, today);
  const ownByDate = new Map<string, OwnMoment[]>();
  for (const one of own) {
    const at = ownByDate.get(one.moment.date) ?? [];
    at.push(one.moment);
    ownByDate.set(one.moment.date, at);
  }
  /*
   * `entries` is the union of both halves, and it is what dots the grid. There
   * is no third dot colour and no third legend entry: the two-colour rule in
   * this file's header is about HOW SOON, not about where a date came from, and
   * every dotted day is named in text in the list beside it.
   */
  const ownEntries: CalendarEntry[] = [...ownByDate.entries()].map(([date, moments]) => ({
    date,
    names: moments.map((moment) => moment.name),
  }));
  /*
   * ONE LIST, BOTH HALVES, AND THIS IS NOT COSMETIC.
   *
   * The grid is fed the union of both halves, so a date the workspace typed
   * gets a dot. If the list were fed only the curated half, that dot would sit
   * on a day with nothing beside it in text — which is precisely the thing this
   * file's header forbids when it argues for two dot colours instead of three:
   * *every dotted day is named in text in the list beside it*. A dot a reader
   * cannot resolve to a name is indistinguishable from a rendering bug.
   *
   * It shipped that way for one commit and was caught by driving the form: a
   * moment added for 15 October dotted the grid, and the list went from the
   * 2nd to the 20th. Merged here rather than in the component, so the grid and
   * the list are fed from the same fold and cannot disagree again.
   *
   * Merged BY DATE: a workspace's "Diwali storewide sale" on the day the
   * dataset already names Diwali is one row carrying both names, which is what
   * the row's `names.join(', ')` was always built for.
   */
  const byDate = new Map<string, { date: string; names: string[]; daysAway: number }>();
  for (const { entry, daysAway } of windowed) {
    byDate.set(entry.date, { date: entry.date, names: [...entry.names], daysAway });
  }
  for (const { moment, daysAway } of own) {
    const at = byDate.get(moment.date);
    if (at === undefined) byDate.set(moment.date, { date: moment.date, names: [moment.name], daysAway });
    else at.names.push(moment.name);
  }
  const merged = [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  return {
    today,
    windowed,
    merged,
    own,
    ownByDate,
    entries: [...windowed.map((one) => one.entry), ...ownEntries],
  };
}

/* -------------------------------------------------------------------------- */
/* The calendar                                                                */
/* -------------------------------------------------------------------------- */

export function CalendarCard({
  calendar,
  now,
}: {
  calendar: AsyncData<CalendarResponse>;
  /** The instant to call "now". Defaults to the real clock; injectable so a suite can set the month. */
  now?: Date;
}) {
  const { data, error, status, reload } = calendar;
  const { today, entries, own } = readerView(data, now);
  const [offset, setOffset] = useState(0);

  const failedOutright = status === 'error' && data === null;
  if (data === null && !failedOutright) return null;
  /* No country means Wend does not know whose calendar applies. Nothing to draw. */
  /*
   * The grid is decoration for the list beside it, which carries every fact in
   * text. It draws when there is something to decorate — a country whose
   * holidays Wend holds, or a date the workspace put there itself. With
   * neither, an undotted grid and a two-colour legend are words and pixels
   * saying nothing.
   *
   * That is narrower than it looks: slice 33 reversed an earlier decision to
   * hide this grid, and that reversal stands. It was argued for a workspace
   * whose dots come from a shipped dataset, and it does not reach a workspace
   * that has no dataset and has written nothing of its own.
   */
  if (data !== null && data.country === null && data.ownMoments.length === 0) return null;

  /*
   * CLAMPED TO WHAT THE CALENDAR ACTUALLY HOLDS.
   *
   * An earlier pass had no arrows, on the argument that they would invite a
   * reader to page into a calendar holding nothing past sixty days. The
   * argument was right and the conclusion was wrong: with one fixed month and a
   * sixty-day window, roughly three quarters of the moments in the list beside
   * this grid fall in a month it never draws, so the two halves of the rail
   * disagree about what is coming.
   *
   * The repair is to bound the paging rather than remove it. The first month is
   * the one holding today; the last is the one holding the end of the window, or
   * the end of the dataset when that comes first. At either end the button is
   * disabled, so there is no month to reach that has nothing in it by
   * construction.
   */
  const windowEnd = addDays(today, CALENDAR_WINDOW_DAYS);
  const coverageEnd = data?.coversUntil ?? null;
  const curatedReach = coverageEnd !== null && coverageEnd < windowEnd ? coverageEnd : windowEnd;
  /*
   * And far enough to reach the workspace's OWN furthest date in the window.
   *
   * Without this, a workspace whose curated dataset runs out in ten days could
   * never page to the launch it put forty days out: the clamp would stop at the
   * dataset's last month and the ‹ › button would be disabled over a month with
   * a dot in it. `furthestOwn` comes from the WINDOWED list, so paging is still
   * bounded by the window and no empty month becomes reachable.
   */
  const furthestOwn = own.at(-1)?.moment.date ?? null;
  const lastDate = furthestOwn !== null && furthestOwn > curatedReach ? furthestOwn : curatedReach;
  const maxOffset = monthsBetween(today, lastDate);
  const clamped = Math.min(Math.max(offset, 0), maxOffset);
  const drawnMonth = shiftMonth(today, clamped);

  const cells = monthCells(today, entries, drawnMonth);
  const monthName = MONTH_NAMES[Number(drawnMonth.slice(5, 7)) - 1] ?? '';
  const dotted = cells.filter((cell) => cell.hasMoment && cell.inMonth).length;

  return (
    <Card data-role="calendar-card">
      <CardHeader className="p-[18px] pb-3.5">
        {/* The sub is the DRAWN month, not a static one, so paging keeps it
            true against the row below. */}
        <CardTitle className="text-[0.95rem]" sub={monthName.slice(0, 3)}>
          Your calendar
        </CardTitle>
      </CardHeader>

      {failedOutright ? (
        <CardContent className="p-[18px] pt-0">
          <ErrorState
            error={error}
            title="Could not load the calendar"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
          />
        </CardContent>
      ) : (
        <CardContent className="p-[18px] pt-0">
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <p className="text-[0.85rem] font-semibold text-text">
              {monthName} {drawnMonth.slice(0, 4)}
            </p>
            {/* Real controls, so they sit OUTSIDE the aria-hidden grid below. */}
            <span className="flex gap-1">
              <button
                type="button"
                aria-label="Previous month"
                disabled={clamped === 0}
                onClick={() => { setOffset(clamped - 1); }}
                className={NAV_BUTTON}
              >
                &lsaquo;
              </button>
              <button
                type="button"
                aria-label="Next month"
                disabled={clamped >= maxOffset}
                onClick={() => { setOffset(clamped + 1); }}
                className={NAV_BUTTON}
              >
                &rsaquo;
              </button>
            </span>
          </div>

          {/* Decoration for the list beside it, which carries every fact in text. */}
          <div aria-hidden="true" className="grid grid-cols-7 gap-0.5">
            {WEEKDAY_INITIALS.map((initial, at) => (
              <span
                // Two Tuesdays and two Saturdays share an initial, so the
                // position is the identity here rather than the letter.
                key={`dow-${String(at)}`}
                className="pb-1 text-center font-mono text-[0.59rem] uppercase tracking-[0.08em] text-muted"
              >
                {initial}
              </span>
            ))}
            {cells.map((cell) => (
              <span
                key={cell.date}
                data-role={
                  !cell.inMonth
                    ? 'calendar-day-out'
                    : cell.hasMoment
                      ? 'calendar-day-marked'
                      : 'calendar-day'
                }
                className={cn(
                  'relative flex aspect-square items-center justify-center rounded-[7px] font-mono text-[0.72rem]',
                  cell.inMonth ? 'text-text' : 'text-muted/35',
                  cell.isToday && 'bg-brand-a/25 font-medium outline outline-1 outline-brand-a',
                )}
              >
                {cell.day}
                {cell.hasMoment && (
                  <span
                    className={cn(
                      'absolute bottom-[3px] left-1/2 size-1 -translate-x-1/2 rounded-full forced-colors:bg-[Highlight]',
                      cell.isSoon ? 'bg-warn' : 'bg-brand-b',
                    )}
                  />
                )}
              </span>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[0.7rem] text-muted">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="size-1.5 rounded-full bg-warn" />
              Within a week
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="size-1.5 rounded-full bg-brand-b" />
              Later this month
            </span>
          </div>

          {/*
            A month with nothing in it says so, rather than the grid vanishing.
            The owner opened the page on such a month and reported the calendar
            missing, which is a worse answer than an undotted September.
          */}
          {dotted === 0 && (
            <p className="mt-2.5 text-[0.7rem] leading-relaxed text-muted">
              Nothing falls in {monthName}. The next one is in the list below.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * How far the calendar actually reaches, when that is less than the window.
 *
 * Without it, the end of the dataset and a quiet two months render the same
 * nothing. The shipped Indian dataset stops on 2027-12-31, so from November 2027
 * the card would claim sixty days over a window it only half covers, and from
 * the end of December it would go silent with no signal.
 */
export function coverageNote(today: string, coversUntil: string | null): string | null {
  if (coversUntil === null) return null;
  const reach = daysBetween(today, coversUntil);
  if (!Number.isFinite(reach) || reach >= CALENDAR_WINDOW_DAYS) return null;
  return `Wend's calendar reaches ${coversUntil}, so this is less than the usual ${String(CALENDAR_WINDOW_DAYS)} days.`;
}

/* -------------------------------------------------------------------------- */
/* The moments                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The list, one row per date.
 *
 * It was one row per festival until review measured that on 66% of days two rows
 * shared a date, 14 April 2026 drew four identical chips, and the alphabetical
 * tie-break printed "Diwali" above "Choti Diwali" on the same day. A marketer
 * plans one campaign per planning day, so a day is the row.
 *
 * **Each row's action names its own moment.** The mockup gives every row a
 * "Create" button, and eight buttons with one accessible name is the
 * indistinguishable-links defect this page has a rule about — so the visible
 * label stays short and `aria-label` carries the moment. The link goes to a
 * blank campaign form: `/campaigns/new` reads no query parameter today, so it
 * cannot arrive pre-filled, and nothing here claims it does.
 */
export function MomentsCard({
  calendar,
  now,
}: {
  calendar: AsyncData<CalendarResponse>;
  now?: Date;
}) {
  const { data, error, status, reload } = calendar;
  const { today, merged } = readerView(data, now);

  /*
   * THE FORM'S STATE, AND THE ONE THING IT DELIBERATELY DOES NOT KEEP.
   *
   * There is no local copy of the moments list. After a successful add or
   * remove the card calls `reload()` and re-renders from what the API answered,
   * so a write that succeeded against a row the next read cannot see is visible
   * rather than papered over by an optimistic update. The cost is one extra
   * round trip on a card that makes one request; the benefit is that the screen
   * can never disagree with the database about what is on this calendar.
   */
  const addRef = useRef<HTMLDetailsElement>(null);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  /*
   * AN INLINE NOTE, NOT A TOAST, AND THE REASON IS NOT ONLY THE HARNESS.
   *
   * `useToast` throws without a `ToastProvider`, which the two suites holding
   * every assertion about this card do not mount — so a toast here would make
   * the result of a successful add unassertable.
   *
   * It is also the better report. The reader is looking at this form; a message
   * beside it is where their eyes already are, and it can say the one thing a
   * toast would have had to say in passing: a moment further out than the
   * window is NOT on the list they are staring at, and is waiting in "Your
   * moments" instead. A toast that vanishes after four seconds is a poor place
   * for the only explanation of why a date somebody just added is not visible.
   */
  const [added, setAdded] = useState<string | null>(null);
  const [removeFailed, setRemoveFailed] = useState<string | null>(null);

  async function onAdd(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;
    setRequestId(undefined);

    /*
     * Checked before the contract runs, because the contract's own message for
     * a missing date is "A date must be written YYYY-MM-DD" — true, and useless
     * to somebody who simply has not picked one yet.
     *
     * The future test is made HERE, against the reader's own wall clock, with
     * zero slack. The server repeats it against UTC with a day of slack,
     * because it does not know the reader's zone and must not refuse a sale
     * starting this evening in Los Angeles. This is the side that knows.
     */
    const missing: FormErrors = {};
    if (date === '') missing.date = OWN_MOMENT_COPY.missingDate;
    else if (parseCalendarDate(date) === null) missing.date = OWN_MOMENT_COPY.unrealDate;
    else if (!ownMomentDateIsAddable(date, today, 0)) missing.date = OWN_MOMENT_COPY.pastDate;
    if (name.trim() === '') missing.name = OWN_MOMENT_COPY.missingName;
    else if (name.trim().length > MAX_OWN_MOMENT_NAME_LENGTH) missing.name = OWN_MOMENT_COPY.longName;
    if (Object.keys(missing).length > 0) {
      setErrors((current) => ({ ...current, ...missing }));
      return;
    }

    const result = validate(addOwnMomentRequestSchema, { date, name: name.trim() });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);
    try {
      /*
       * IMPORTED HERE, NOT AT THE TOP, AND THE REASON IS THE TEST HARNESS.
       *
       * `lib/api/overview` reaches `lib/config.ts`, which reads
       * `import.meta.env`. `renderToStaticMarkup` has no such object, so a
       * static import would make this whole module unmountable by
       * `verify-calendar` and `verify-dashboard` — the two suites that hold
       * every assertion about this card. A card whose behaviour cannot be
       * asserted in order to make one line tidier is the wrong trade.
       *
       * Deferring it costs nothing at runtime: the module is fetched the first
       * time somebody actually submits, by which point the app is long since
       * booted, and the handler is already async.
       */
      const { addOwnMoment } = await import('../../lib/api/overview');
      const moment = await addOwnMoment(result.data);
      const away = daysBetween(today, moment.date);
      setAdded(
        Number.isFinite(away) && away <= CALENDAR_WINDOW_DAYS
          ? 'Added. It is on your calendar.'
          : `Added. It is more than ${String(CALENDAR_WINDOW_DAYS)} days out, so it waits in "Your moments" until it is closer.`,
      );
      setDate('');
      setName('');
      setPending(false);
      if (addRef.current !== null) addRef.current.open = false;
      reload();
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  async function onRemove(momentId: string): Promise<void> {
    if (removing !== null) return;
    setRemoving(momentId);
    try {
      const { removeOwnMoment } = await import('../../lib/api/overview');
      await removeOwnMoment(momentId);
      reload();
    } catch (error) {
      /*
       * A failed removal is a toast rather than a field error: there is no
       * field, and the row is still on screen, which is itself the truthful
       * report that nothing was removed.
       */
      setRemoveFailed(errorMessage(error));
    } finally {
      setRemoving(null);
    }
  }


  const failedOutright = status === 'error' && data === null;
  if (data === null && !failedOutright) return null;
  /*
   * NO COUNTRY GATE HERE, AND ITS ABSENCE IS THE FEATURE.
   *
   * This card used to return null for a workspace with no country. Two of the
   * three live workspaces have none — and this card holds the only way to add a
   * moment, so the gate hid the affordance for adding the first one behind the
   * condition that you already had one. A card that hides until you have a
   * moment can never be used to get your first.
   *
   * The Munich-hotel rule is untouched: it governs which COUNTRY'S holidays
   * Wend may assert, and it is enforced where that decision is made, in
   * `readCalendar`. A date somebody typed themselves is not an assumption about
   * anybody's country.
   */

  return (
    <Card data-role="moments-card">
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]" sub="yours">
          Moments coming up
        </CardTitle>
      </CardHeader>

      <CardContent className="p-[18px] pt-0">
        {failedOutright && (
          <ErrorState
            error={error}
            title="Could not load what is coming up"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
          />
        )}

        {data !== null && merged.length === 0 && (
          <p className="text-[0.75rem] leading-relaxed text-muted">
            Nothing in the next {CALENDAR_WINDOW_DAYS} days.
          </p>
        )}

        {merged.length > 0 && (
          <ul data-role="moment-list" className="flex flex-col">
            {merged.map((entry) => {
              const { daysAway } = entry;
              const names = entry.names.join(', ');
              return (
                <li
                  key={entry.date}
                  data-role="moment"
                  className="flex items-center gap-2.5 border-t border-line py-2.5 first:border-t-0 first:pt-0"
                >
                  {/*
                    FIXED WIDTH, NOT INTRINSIC.
                    "1" and "28" are different widths, so a chip sized to its
                    own content steps the titles beside it left and right down
                    the list. At 310px that ragged edge is the first thing the
                    eye catches. 38px holds two digits and a three-letter month
                    with room to spare, and every title then starts at the same
                    x.
                  */}
                  <span className="w-10 flex-none rounded-[9px] border border-line-strong py-1 text-center font-mono">
                    <span className="block text-[0.6rem] uppercase leading-none tracking-[0.08em] text-muted">
                      {(MONTH_NAMES[Number(entry.date.slice(5, 7)) - 1] ?? '').slice(0, 3)}
                    </span>
                    <span className="block text-[0.85rem] font-medium leading-tight">
                      {entry.date.slice(8, 10)}
                    </span>
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block text-[0.8rem] font-medium leading-snug text-text">
                      {names}
                    </span>
                    <span
                      className={cn(
                        'block font-mono text-[0.68rem]',
                        daysAway <= CALENDAR_SOON_DAYS ? 'text-warn' : 'text-muted',
                      )}
                    >
                      {daysAwayLabel(daysAway)}
                    </span>
                  </span>

                  <Link
                    to="/campaigns/new"
                    aria-label={`Create a campaign for ${names}`}
                    className={cn(
                      'flex-none rounded-sm border border-line-strong px-2 py-1 text-[0.72rem] text-text',
                      'hover:border-white/25 hover:bg-white/[0.06]',
                    )}
                  >
                    Create
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        {/* Said only when it is true: the dataset running out is not a quiet stretch. */}
        {data !== null && coverageNote(today, data.coversUntil) !== null && (
          <p className="pt-2.5 text-[0.7rem] leading-relaxed text-muted">
            {coverageNote(today, data.coversUntil)}
          </p>
        )}

        {/*
          YOUR MOMENTS — EVERY ONE, PAST INCLUDED.

          The list above is cut to the reader's sixty-day window, which is right
          for a list headed "coming up". This one is not cut at all, and that is
          what makes removal possible: a moment forty days past, or four months
          out, would otherwise be invisible on this card and therefore
          impossible to take off it. A list you can add to and never remove from
          is a trap, and an unbounded read is what stops this being one.

          A `<details>` rather than a conditional region for the reason
          `AdvancedDisclosure` records: unmounted JSX is not on the page, so
          Ctrl+F cannot find it. Somebody who typed "Annual conference" can
          search for it with this shut.
        */}
        {data !== null && data.ownMoments.length > 0 && (
          <details data-role="own-moments" className="mt-3">
            <summary className="cursor-pointer text-[0.72rem] text-muted hover:text-text">
              Your moments ({data.ownMoments.length})
            </summary>
            {removeFailed !== null && (
              <p role="alert" className="mt-2 text-[0.72rem] leading-relaxed text-bad">
                Could not remove that moment. {removeFailed}
              </p>
            )}
            <ul className="mt-2 flex flex-col">
              {data.ownMoments.map((moment) => (
                <li
                  key={moment.id}
                  data-role="own-moment"
                  className="flex items-center gap-2 border-t border-line py-2 first:border-t-0"
                >
                  <span className="flex-none font-mono text-[0.66rem] text-muted">
                    {formatMomentDate(moment.date)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[0.75rem] text-text">
                    {moment.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${moment.name} on ${formatMomentDate(moment.date)}`}
                    disabled={removing === moment.id}
                    onClick={() => void onRemove(moment.id)}
                    className={cn(
                      'flex-none rounded-sm border border-line-strong px-2 py-0.5 text-[0.68rem] text-muted',
                      'hover:border-white/25 hover:text-text disabled:opacity-50',
                    )}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}

        {/*
          The approved design's dashed row, and it now does what it says.

          It was a `<Link to="/campaigns/new">`: a control that promised to add
          a moment and opened a campaign form. It is now the `<summary>` of an
          always-mounted `<details>`, so the dashed box looks identical and the
          form expands underneath it in the rail.

          UNCONTROLLED, closed imperatively after a successful add. Not
          `open={...}` with `onToggle`: that spends the exact benefit
          `AdvancedDisclosure`'s header buys — the element owns the state, the
          keyboard behaviour and the disclosure role with no code of ours — and
          React and the browser's own toggling can then disagree and shut the
          panel over a half-typed name.
        */}
        {data !== null && (
          <details ref={addRef} className="mt-3">
            <summary
              data-role="add-moment"
              className={cn(
                'block w-full cursor-pointer list-none rounded-base border border-dashed border-line-strong px-3 py-2.5',
                'text-center text-[0.78rem] text-muted',
                'hover:border-white/25 hover:text-text',
                '[&::-webkit-details-marker]:hidden',
              )}
            >
              + Add a moment
            </summary>

            <form onSubmit={(event) => void onAdd(event)} noValidate className="mt-3 flex flex-col gap-3">
              {errors[ROOT_FIELD] !== undefined && (
                <FormAlert tone="error" requestId={requestId}>
                  {errors[ROOT_FIELD]}
                </FormAlert>
              )}

              <FormField label="Date" error={errors.date}>
                <Input
                  type="date"
                  value={date}
                  /* An affordance only — the form is `noValidate`, so the
                     refusal is written out below rather than left to the
                     browser's own bubble, which this app never uses. */
                  min={today}
                  disabled={pending}
                  onChange={(event) => {
                    setDate(event.target.value);
                    setAdded(null);
                    setErrors((current) => withoutError(current, 'date'));
                  }}
                />
              </FormField>

              <FormField label="What is it" error={errors.name}>
                <Input
                  type="text"
                  value={name}
                  maxLength={MAX_OWN_MOMENT_NAME_LENGTH}
                  placeholder="Spring sale, launch day, store closed"
                  disabled={pending}
                  onChange={(event) => {
                    setName(event.target.value);
                    setErrors((current) => withoutError(current, 'name'));
                  }}
                />
              </FormField>

              {/* No cancel: the summary closes it, and `Button`'s own comment
                  warns that a secondary button inside a form needs an explicit
                  type="button" — a trap not worth walking into for a control
                  the disclosure already provides. */}
              <Button type="submit" size="sm" loading={pending} className="self-start">
                Add
              </Button>

              {added !== null && (
                <p data-role="moment-added" role="status" className="text-[0.72rem] leading-relaxed text-good">
                  {added}
                </p>
              )}
            </form>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
